import React, { useState } from 'react';
import { Grid, CircularProgress, Alert } from '@mui/material';

import {
  MyCertificateCard,
  RequiredCertificateCard,
  ExpiredCertificateCard,
  CertificationTabs,
  CertificationHeader,
  EmptyState,
  RequiredCertificateDetailsModal,
} from './components';
import Header from 'modules/views/shared/Header';
import Pagination from 'modules/views/shared/Pagination';

import { useCertificationList } from './hooks/useCertificationList';
import { useCertificationTypeOptions } from './hooks/useCertificationTypeOptions';
import { useProjects } from './hooks/useProjects';

// Import modals and hooks from Certifications page
import { AddCertificationModal, Certificate as CertificationsPageCertificate } from '../Certifications/components';
import { useCertificationModal } from '../Certifications/hooks';
import { useMyCertificationOperations } from './hooks/useMyCertificationOperations';
import DeleteMyCertificationModal from './components/DeleteMyCertificationModal';

import { Certification, RequiredCertificate, FilterState, TabIndex } from './types';
import { useStyles } from './styles';

const MyCertifications = () => {
  const classes = useStyles();
  // Per-tab filter state for independent filtering
  const [filterStates, setFilterStates] = useState<{ [key: number]: FilterState }>({
    [TabIndex.REQUIRED]: { search: '', selectedOptions: [] },
    [TabIndex.EXPIRED]: { search: '', selectedOptions: [] },
    [TabIndex.CURRENT]: { search: '', selectedOptions: [] },
  });
  const [deleteCertification, setDeleteCertification] = useState<CertificationsPageCertificate | null>(null);
  const [preselectedCertificateId, setPreselectedCertificateId] = useState<string | null>(null);
  const [detailsModalData, setDetailsModalData] = useState<RequiredCertificate | null>(null);
  const [selectedRequiredCertificate, setSelectedRequiredCertificate] = useState<RequiredCertificate | null>(null);

  const {
    certificationList,
    requiredCertificateList,
    certificationCounts,
    isLoading,
    error,
    activeTab,
    page,
    handleTabChange,
    handlePageChange,
    paginationInfo,
    handleSortChange,
    sortType,
    isWorker,
    handleFilterChange,
  } = useCertificationList();

  // Get typeOptions for the modal (without needing projectId)
  const { typeOptions } = useCertificationTypeOptions();

  // Load projects for filter
  const { projects, isLoading: isLoadingProjects } = useProjects();

  // Modal state and operations
  const { modalState, openAddModal, openEditModal, closeModal, setEditData, setLoading } = useCertificationModal();
  const { saveCertification, loadCertificationDetails } = useMyCertificationOperations(
    typeOptions,
    modalState,
    setEditData,
    setLoading,
    closeModal,
  );

  const handleEdit = async (certification: Certification) => {
    openEditModal(certification.id);
    try {
      await loadCertificationDetails(certification.id);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleDelete = (certification: Certification) => {
    // Convert MyCertifications type to Certifications page type
    const certToDelete: CertificationsPageCertificate = {
      id: certification.id,
      idNumber: certification.certificationId || '',
      completionDate: certification.issueDate,
      expirationDate: certification.expirationDate || null,
      createdAt: certification.issueDate, // Use issueDate as createdAt
      lastUpdatedAt: certification.expirationDate || certification.issueDate, // Use expirationDate or issueDate
      description: certification.description || '',
      certification: {
        id: certification.id,
        name: certification.name,
        isExpirationDateMandatory: false,
      },
      project: null,
      alias: null,
      aliasesInProjects: [],
      projectVisibility: 0,
    };
    setDeleteCertification(certToDelete);
  };

  const handleAddCertificate = (certificate: RequiredCertificate) => {
    setPreselectedCertificateId(null);
    setSelectedRequiredCertificate(certificate);
    openAddModal();
  };

  const handleViewDetails = (certificate: RequiredCertificate) => {
    setDetailsModalData(certificate);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalData(null);
  };

  const handleAddCertificateFromDetails = () => {
    if (detailsModalData) {
      handleAddCertificate(detailsModalData);
    }
  };

  const handleUploadNew = (certification: Certification) => {
    setPreselectedCertificateId(certification.certificationTypeId || null);
    openAddModal();
  };

  const handleSaveCertification = async (certificationData: any) => {
    // Map activeTab to filterCertificateStatus
    // TabIndex.REQUIRED = 0 -> FilterCertificateStatus.REQUIRED = 2
    // TabIndex.EXPIRED = 1 -> FilterCertificateStatus.EXPIRED = 1
    // TabIndex.CURRENT = 2 -> FilterCertificateStatus.CURRENT = 0
    const filterStatusMap = {
      [TabIndex.REQUIRED]: 2,
      [TabIndex.EXPIRED]: 1,
      [TabIndex.CURRENT]: 0,
    };

    await saveCertification(certificationData, {
      pageNumber: page,
      pageSize: 15,
      filterCertificateStatus: filterStatusMap[activeTab],
      sortCriteria: sortType,
      projectIds:
        filterStates[activeTab].selectedOptions.length > 0 ? filterStates[activeTab].selectedOptions : undefined,
    });
  };

  const handleCloseModal = () => {
    setPreselectedCertificateId(null);
    setSelectedRequiredCertificate(null);
    closeModal();
  };

  const handleCloseDeleteModal = () => {
    setDeleteCertification(null);
  };

  const handleDeleteSuccess = () => {
    // Refresh is handled by deleteMyCertification action
    handleCloseDeleteModal();
  };

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handlePageChange(value);
  };

  const onFilterChange = (newFilterState: FilterState) => {
    setFilterStates(prev => ({ ...prev, [activeTab]: newFilterState }));
    // Reload certifications with selected project filters
    handleFilterChange(newFilterState.selectedOptions, newFilterState.noRelatedProject || false);
  };

  const renderCertificationsContent = () => {
    // Render Required Certificates
    if (activeTab === TabIndex.REQUIRED) {
      if (requiredCertificateList.length === 0) {
        return <EmptyState activeTab={activeTab} />;
      }

      return (
        <Grid container spacing={3} className={classes.certificationsGrid} data-testid="my-certifications-grid">
          {requiredCertificateList.map(certificate => (
            <Grid item xs={12} sm={6} md={4} key={certificate.id}>
              <RequiredCertificateCard
                certificate={certificate}
                onAddCertificate={handleAddCertificate}
                onViewDetails={handleViewDetails}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    // Render Expired Certificates
    if (activeTab === TabIndex.EXPIRED) {
      if (certificationList.length === 0) {
        return <EmptyState activeTab={activeTab} />;
      }

      return (
        <Grid container spacing={3} className={classes.certificationsGrid} data-testid="my-certifications-grid">
          {certificationList.map(certification => (
            <Grid item xs={12} sm={6} md={4} key={certification.id}>
              <ExpiredCertificateCard certification={certification} onUploadNew={handleUploadNew} />
            </Grid>
          ))}
        </Grid>
      );
    }

    // Render Current Certificates
    if (certificationList.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    return (
      <Grid container spacing={3} className={classes.certificationsGrid} data-testid="my-certifications-grid">
        {certificationList.map(certification => (
          <Grid item xs={12} sm={6} md={4} key={certification.id}>
            <MyCertificateCard certification={certification} onEdit={handleEdit} onDelete={handleDelete} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={`${classes.container} ${!isWorker ? classes.containerWithWarning : ''}`}>
      <Header styleClass={classes.mainHeader} showOnlineWarning={!isWorker} />

      {/* Header */}
      <CertificationHeader />

      {/* Tabs */}
      <CertificationTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        certificationCounts={certificationCounts}
        onSortChange={handleSortChange}
        sortType={sortType}
        onFilterChange={onFilterChange}
        filterState={filterStates[activeTab]}
        onAddCertification={openAddModal}
        projects={projects}
        isLoadingProjects={isLoadingProjects}
      />

      {/* Loading State */}
      {isLoading && (
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
          data-testid="my-certifications-loader"
        >
          <CircularProgress />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div style={{ padding: '20px' }} data-testid="my-certifications-error">
          <Alert severity="error">{error}</Alert>
        </div>
      )}

      {/* Certifications Content */}
      <div className={classes.content} data-testid="my-certifications-content">
        {!isLoading && !error && renderCertificationsContent()}
      </div>

      {/* Pagination */}
      {!isLoading && !error && paginationInfo && paginationInfo.totalResults > 0 && (
        <div className={classes.paginationWrapper}>
          <Pagination
            count={Math.ceil(paginationInfo.totalResults / paginationInfo.pageSize)}
            page={page}
            onChange={onPageChange}
            siblingCount={1}
            boundaryCount={3}
            testId="my-certifications-pagination"
          />
        </div>
      )}

      {/* Modals */}
      <AddCertificationModal
        isVisible={modalState.isOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCertification}
        typeOptions={typeOptions}
        editData={modalState.editData}
        isEditMode={modalState.mode === 'edit'}
        isLoadingEditDetails={modalState.isLoadingDetails}
        isMyCertifications={true}
        preselectedCertificateId={preselectedCertificateId}
        disableCertificateTypeSelection={preselectedCertificateId !== null}
        certificationGroups={selectedRequiredCertificate?.certificationGroups || undefined}
      />
      <DeleteMyCertificationModal
        isVisible={!!deleteCertification?.id}
        certificate={deleteCertification}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleDeleteSuccess}
        pageNumber={page}
        pageSize={15}
        filterCertificateStatus={0}
        sortCriteria={sortType}
        projectIds={
          filterStates[activeTab].selectedOptions.length > 0 ? filterStates[activeTab].selectedOptions : undefined
        }
      />
      <RequiredCertificateDetailsModal
        isVisible={!!detailsModalData}
        projectName={detailsModalData?.projectName || ''}
        certificationGroups={detailsModalData?.certificationGroups || []}
        onClose={handleCloseDetailsModal}
        onAddCertificate={handleAddCertificateFromDetails}
      />
    </div>
  );
};

export default MyCertifications;
